import {createBehavior} from '@area17/a17-behaviors';

const navigationMenus = createBehavior('navigationMenus',
  {
    toggleDropdown(event) {
      const dropdownId = event.currentTarget.dataset.dropdownId;
      const isMobile = window.innerWidth < 900;

      // On mobile, topics opens as overlay
      if (isMobile && dropdownId === 'topics') {
        this.openTopicsOverlay(event.currentTarget);
        return;
      }

      const dropdown = this.$node.querySelector(`#nav-dropdown-${dropdownId}`);
      if (!dropdown) return;

      if (!dropdown.classList.contains('hidden')) {
        this.closeAllNavs();
        return;
      }
      this.closeAllNavs(true);
      dropdown.classList.remove('hidden');
      event.currentTarget.classList.add('is-open');
      document.body.classList.add('is-dropdown-active');
    },

    openTopicsOverlay(btn) {
      const overlay = this.$node.querySelector('#nav-dropdown-topics');
      if (!overlay) return;
      overlay.classList.remove('hidden');
      requestAnimationFrame(() => {
        overlay.classList.add('is-topics-open');
      });
      document.body.classList.add('is-topics-open');
      if (btn) btn.classList.add('is-open');
    },

    closeTopicsOverlay(immediate = false) {
      const overlay = this.$node.querySelector('#nav-dropdown-topics');
      if (!overlay) return;
      const isMobile = window.innerWidth < 900;
      // On desktop topics is just a regular hidden-toggled dropdown — nothing to do here
      if (!isMobile) return;
      if (!overlay.classList.contains('is-topics-open')) return;
      overlay.querySelectorAll('.topics-child-panel.is-active').forEach(p => p.classList.remove('is-active'));
      overlay.classList.remove('has-child-open', 'is-topics-open');
      document.body.classList.remove('is-topics-open');
      const topicsBtn = this.$node.querySelector('[data-dropdown-id="topics"]');
      if (topicsBtn) topicsBtn.classList.remove('is-open');
      // When the parent nav container is animating out (hamburger close), the
      // overlay's own transitionend never fires — skip the listener and hide immediately.
      if (immediate) {
        overlay.classList.add('hidden');
      } else {
        overlay.addEventListener('transitionend', () => {
          overlay.classList.add('hidden');
        }, { once: true });
      }
    },

    positionMemberFlyout() {
      const flyout = document.getElementById('member-flyout');
      const trigger = document.getElementById('member-flyout-trigger');
      if (!flyout || !trigger) return;

      const rect = trigger.getBoundingClientRect();
      flyout.style.top = `${rect.bottom + 8}px`;
      flyout.style.right = `${window.innerWidth - rect.right}px`;
    },

    openMemberFlyout() {
      const flyout = document.getElementById('member-flyout');
      const trigger = document.getElementById('member-flyout-trigger');
      const arrow = document.getElementById('member-flyout-arrow');
      if (!flyout || !trigger) return;

      this.closeAllNavs();

      // Move to body to escape header stacking context
      if (flyout.parentElement !== document.body) {
        document.body.appendChild(flyout);
      }

      this.positionMemberFlyout();
      window.addEventListener('scroll', this.positionMemberFlyout, { passive: true });

      flyout.classList.remove('hidden');
      requestAnimationFrame(() => {
        flyout.classList.add('is-open');
      });
      if (arrow) arrow.classList.add('is-open');
    },

    closeMemberFlyout() {
      const flyout = document.getElementById('member-flyout');
      const arrow = document.getElementById('member-flyout-arrow');
      if (!flyout) return;
      if (!flyout.classList.contains('is-open')) return;
      window.removeEventListener('scroll', this.positionMemberFlyout);
      flyout.classList.remove('is-open');
      if (arrow) arrow.classList.remove('is-open');
      flyout.addEventListener('transitionend', () => {
        flyout.classList.add('hidden');
      }, { once: true });
    },

    toggleHamburger() {
      const navContents = document.getElementById('nav-contents');
      const hamburger = document.getElementById('hamburger-toggle');
      const isOpen = document.body.classList.contains('is-nav-open');

      if (isOpen) {
        document.body.classList.remove('is-nav-open');
        hamburger.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('is-dropdown-active');
        this.closeTopicsOverlay(true);
      } else {
        document.body.classList.add('is-nav-open');
        hamburger.setAttribute('aria-expanded', 'true');
      }
    },

    closeAllNavs(preserveMask = false) {
      if (preserveMask !== true) {
        preserveMask = false;
      }
      const buttons = this.$node.querySelectorAll('[data-navigationMenus-nav]');
      for (let i = 0; i < buttons.length; i++) {
        const dropdownId = buttons[i].dataset.dropdownId;
        const dropdown = this.$node.querySelector(`#nav-dropdown-${dropdownId}`);
        if (dropdown) dropdown.classList.add('hidden');
        buttons[i].classList.remove('is-open');
      }
      this.closeTopicsOverlay();
      this.closeMemberFlyout();
      if (!preserveMask) {
        document.body.classList.remove('is-dropdown-active');
      }
    },
  },
  {
    init() {
      // Hamburger
      const hamburger = document.getElementById('hamburger-toggle');
      if (hamburger) {
        hamburger.addEventListener('click', this.toggleHamburger.bind(this));
      }

      // Use event delegation for all nav buttons and close button
      // so they work regardless of cache or Datastar patch timing
      this.$node.addEventListener('click', (e) => {
        const navBtn = e.target.closest('[data-navigationMenus-nav]');
        if (navBtn) {
          this.toggleDropdown({ currentTarget: navBtn });
          return;
        }
        if (e.target.closest('#topics-overlay-close')) {
          this.closeTopicsOverlay();
          return;
        }
        // Drill into child panel
        const drillBtn = e.target.closest('.topics-drill-btn');
        if (drillBtn) {
          const topicId = drillBtn.dataset.topicId;
          const childPanel = this.$node.querySelector(`#topics-child-${topicId}`);
          const wrap = this.$node.querySelector('#nav-dropdown-topics');
          if (childPanel && wrap) {
            wrap.classList.add('has-child-open');
            childPanel.classList.add('is-active');
          }
          return;
        }
        // Back button — close child panel
        const backBtn = e.target.closest('.topics-back-btn');
        if (backBtn) {
          const childPanel = backBtn.closest('.topics-child-panel');
          const wrap = this.$node.querySelector('#nav-dropdown-topics');
          if (childPanel && wrap) {
            childPanel.classList.remove('is-active');
            wrap.classList.remove('has-child-open');
          }
        }
      });

      // Keep navButtons in sync for closeAllNavs loop
      document.addEventListener('menu:loaded', () => {
        this.navButtons = this.getChildren('nav');
      });

      // Initialise navButtons immediately in case cache serves them synchronously
      this.navButtons = this.getChildren('nav');

      document.addEventListener('menu:close', this.closeAllNavs.bind(this));
      document.addEventListener('mask:clicked', this.closeAllNavs.bind(this));

      // Member dropdown — delegated since it's patched in by Datastar
      document.addEventListener('click', (e) => {
        const explicitTrigger = e.target.closest('#member-flyout-trigger');
        const usernameTrigger = e.target.closest('[data-member-flyout-trigger]');
        const isMobile = window.innerWidth < 900;
        const trigger = explicitTrigger || (isMobile && usernameTrigger);
        const flyout = document.getElementById('member-flyout');

        if (trigger) {
          // Toggle on re-click
          if (flyout && flyout.classList.contains('is-open')) {
            this.closeMemberFlyout();
          } else {
            this.openMemberFlyout();
          }
          return;
        }

        if (e.target.closest('#member-flyout-close')) {
          this.closeMemberFlyout();
          return;
        }

        // Close when clicking outside
        if (flyout && flyout.classList.contains('is-open') && !e.target.closest('#NavPersonalizationUsername')) {
          this.closeMemberFlyout();
        }
      });

      window.addEventListener('resize', () => {
        if (window.innerWidth >= 900) {
          this.closeTopicsOverlay();
          document.body.classList.remove('is-nav-open');
        }
      });
    }
  }
);

export default navigationMenus;
